import React, { useMemo } from 'react';
import useBreakpoint from '../hooks/useBreakpoint';
import { useTheme } from '../ThemeContext';
import { getCurrencySymbol } from '../categories';
import {
  AreaChart, Area, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const ANNUAL = {
  Daily: 365, Weekly: 52, Biweekly: 26, Monthly: 12,
  Quarterly: 4, 'Semi-annually': 2, Yearly: 1,
};
const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const PIE_COLORS = ['#0061a4','#FF9800','#9a25ae','#00BCD4','#FF5722','#1b6d24','#E91E63','#607D8B','#795548','#3F51B5'];

const fmtK   = (n) => (Math.abs(n) >= 1000 ? (n / 1000).toFixed(1) + 'k' : Math.round(n).toString());
const fmtDolBase = (n, sym) => (n < 0 ? '-' : '') + (sym || '$') + Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const toMonthly = (item) => (item.amount * ANNUAL[item.frequency]) / 12;

// ─── Upcoming occurrences ─────────────────────────────────────────────────────
function itemHitsOnDate(item, date) {
  const y = date.getFullYear(), m = date.getMonth(), d = date.getDate();
  const lastOcc = item.lastOccurrence ? new Date(item.lastOccurrence + 'T00:00:00') : null;
  switch (item.frequency) {
    case 'Daily': return true;
    case 'Weekly':
      if (lastOcc) { const diff = Math.round((date - lastOcc) / 86400000); return diff % 7 === 0; }
      return date.getDay() === 1;
    case 'Biweekly':
      if (lastOcc) { const diff = Math.round((date - lastOcc) / 86400000); return diff % 14 === 0; }
      return false;
    case 'Monthly':
      return lastOcc ? d === lastOcc.getDate() : d === (item.dayOfMonth || 1);
    case 'Quarterly': {
      const ref = lastOcc || new Date(y, 0, 1);
      const md = (y - ref.getFullYear()) * 12 + (m - ref.getMonth());
      return md % 3 === 0 && d === (lastOcc ? lastOcc.getDate() : item.dayOfMonth || 1);
    }
    case 'Semi-annually': {
      const ref = lastOcc || new Date(y, 0, 1);
      const md = (y - ref.getFullYear()) * 12 + (m - ref.getMonth());
      return md % 6 === 0 && d === (lastOcc ? lastOcc.getDate() : item.dayOfMonth || 1);
    }
    case 'Yearly':
      return lastOcc ? m === lastOcc.getMonth() && d === lastOcc.getDate() : m === 0 && d === 1;
    default: return false;
  }
}

function getUpcoming(budgetItems, days = 30) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const results = [];
  for (let i = 1; i <= days; i++) {
    const cursor = new Date(today.getTime() + i * 86400000);
    for (const item of budgetItems) {
      if (item.frequency === 'Daily') continue; // skip daily — too many
      if (itemHitsOnDate(item, cursor)) {
        results.push({ ...item, dueDate: new Date(cursor) });
      }
    }
  }
  return results.sort((a, b) => a.dueDate - b.dueDate);
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function Dashboard({ budgetItems, debtPaymentItems = [], endOfDayBalance, debts, spendingLog = [], isAuthenticated, isPro, onUpgradeClick, currency }) {
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const isDesktop = bp === 'desktop';
  const { t, isDark } = useTheme();
  const sym = getCurrencySymbol(currency);
  const fmtDol = (n) => fmtDolBase(n, sym);
  const [projMonths, setProjMonths] = React.useState(isPro ? 3 : 0);

  // Recalc when isPro changes
  React.useEffect(() => {
    if (!isPro) setProjMonths(0);
    else if (projMonths === 0) setProjMonths(3);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro]);
  const monthlyIncome   = useMemo(() => budgetItems.filter(i => i.type === 'income').reduce((s, i) => s + toMonthly(i), 0), [budgetItems]);
  const monthlyExpenses = useMemo(() => budgetItems.filter(i => i.type !== 'income').reduce((s, i) => s + toMonthly(i), 0), [budgetItems]);
  const netMonthly      = monthlyIncome - monthlyExpenses;
  const totalDebt       = useMemo(() => debts.reduce((s, d) => s + (d.balance || 0), 0), [debts]);
  const netWorth        = endOfDayBalance - totalDebt;

  // Monthly debt payment outflow (from chequing — tracked separately to avoid double-count in net worth)
  const totalMonthlyDebtPayments = useMemo(() =>
    debtPaymentItems.reduce((s, p) => s + toMonthly(p), 0),
  [debtPaymentItems]);

  // Weighted monthly interest on current debt balances
  const totalMonthlyInterest = useMemo(() =>
    debts.reduce((s, d) => s + (d.balance || 0) * (d.interestRate / 100) / 12, 0),
  [debts]);

  // Multi-month projection (configurable, Pro: 3/6/12 months, Free: current month only)
  const projectionData = useMemo(() => {
    const today = new Date();
    const data  = [];
    let chequing = endOfDayBalance;
    let debtBal  = totalDebt;
    const months = projMonths === 0 ? 0 : projMonths;
    for (let i = 0; i <= months; i++) {
      const d   = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const lbl = MONTH_LABELS[d.getMonth()] + (i === 0 || d.getMonth() === 0 ? ' \'' + String(d.getFullYear()).slice(2) : '');
      const nw  = Math.round(chequing - debtBal);
      const netMo = Math.abs(monthlyIncome - monthlyExpenses - totalMonthlyDebtPayments);
      data.push({
        month: lbl,
        Balance: Math.round(chequing),
        Optimistic: Math.round(chequing + i * netMo * 0.15),
        Conservative: Math.round(chequing - i * netMo * 0.15),
        Income: Math.round(monthlyIncome),
        Expenses: Math.round(monthlyExpenses),
        NetWorth: nw,
      });
      chequing += monthlyIncome - monthlyExpenses - totalMonthlyDebtPayments;
      const monthInt = debtBal > 0 ? totalMonthlyInterest : 0;
      debtBal = Math.max(0, debtBal + monthInt - totalMonthlyDebtPayments);
    }
    return data;
  }, [endOfDayBalance, monthlyIncome, monthlyExpenses, totalDebt, totalMonthlyDebtPayments, totalMonthlyInterest, projMonths]);

  // Expense pie slices — grouped by category
  const expenseSlices = useMemo(() => {
    const byCategory = {};
    budgetItems
      .filter(i => i.type !== 'income' && i.amount > 0)
      .forEach(i => {
        const cat = i.category || 'Other Expenses';
        byCategory[cat] = (byCategory[cat] || 0) + toMonthly(i);
      });
    return Object.entries(byCategory)
      .map(([name, value]) => ({ name, value: parseFloat(value.toFixed(2)) }))
      .filter(i => i.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [budgetItems]);

  const upcoming = useMemo(() => getUpcoming(budgetItems, 30), [budgetItems]);

  const isEmpty = budgetItems.length === 0;

  // Responsive sizing
  const pagePadding = isDesktop ? '20px' : isTablet ? '16px' : '10px';
  const thPadding = isDesktop ? '10px 16px' : isTablet ? '8px 12px' : '6px 8px';
  const tdPadding = isDesktop ? '10px 16px' : isTablet ? '8px 12px' : '6px 8px';
  const tableFontSize = isDesktop ? '13px' : isTablet ? '12px' : '12px';
  const sectionTitleFontSize = isDesktop ? '16px' : isTablet ? '15px' : '14px';
  const cardsGridCols = isDesktop
    ? 'repeat(auto-fill, minmax(200px, 1fr))'
    : 'repeat(2, 1fr)';

  return (
    <div style={{ ...s.page, background: t.pageBg, padding: pagePadding }}>
      <div style={s.container}>
        <h1 style={{ ...s.pageTitle, color: t.text }}>Dashboard</h1>

        {/* ── Summary cards ── */}
        <div style={{ ...s.cardsRow, display: 'grid', gridTemplateColumns: cardsGridCols }}>
          <StatCard label="Monthly Income"   value={fmtDol(monthlyIncome)}   accent="#1b6d24" />
          <StatCard label="Monthly Expenses" value={fmtDol(monthlyExpenses)} accent="#ba1a1a" />
          <StatCard
            label="Net Monthly"
            value={fmtDol(netMonthly)}
            accent={netMonthly >= 0 ? '#1b6d24' : '#ba1a1a'}
            sub={netMonthly >= 0 ? 'Surplus' : 'Deficit'}
          />
          <StatCard label="Current Balance" value={fmtDol(endOfDayBalance)} accent="#0061a4" />
          {totalDebt > 0 && (
            <StatCard label="Total Debt" value={fmtDol(totalDebt)} accent="#e65100" />
          )}
          <StatCard
            label="Net Worth"
            value={fmtDol(netWorth)}
            accent={netWorth >= 0 ? '#0061a4' : '#ba1a1a'}
            sub="Balance − Debt"
          />
        </div>

        {isEmpty ? (
          <div style={{ ...s.emptyState, background: t.card }}>
            <p style={{ fontSize: '18px', color: t.textMuted, margin: 0 }}>
              Add income sources and bills in the Budget Planner to see your dashboard.
            </p>
            <p style={{ fontSize: '13px', color: t.textFaintest, marginTop: '8px' }}>Use the Budget tab above to get started.</p>
          </div>
        ) : (
          <>
            {/* ── Charts row ── */}
            <div style={s.chartsRow}>
              {/* Projected balance line chart */}
              <div style={{ ...s.chartCard, background: t.card, flex: 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                  <h3 style={{ ...s.chartTitle, margin: 0, color: t.text, fontSize: sectionTitleFontSize }}>Balance Projection</h3>
                  {isPro ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {[3, 6, 12].map(m => (
                        <button key={m} onClick={() => setProjMonths(m)} style={{
                          padding: '3px 8px', fontSize: '11px', fontWeight: projMonths === m ? '700' : '400',
                          border: projMonths === m ? '2px solid #0061a4' : `1px solid ${t.borderFaint}`,
                          borderRadius: '4px', background: projMonths === m ? '#d1e4ff' : t.card,
                          color: projMonths === m ? '#0061a4' : t.textSecondary,
                          cursor: 'pointer', fontFamily: 'inherit',
                        }}>
                          {m}mo
                        </button>
                      ))}
                    </div>
                  ) : (
                    <button onClick={onUpgradeClick} style={{
                      fontSize: '11px', fontWeight: '600', padding: '3px 10px',
                      background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
                      color: 'white', border: 'none', borderRadius: '8px',
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}>
                      Upgrade for projections
                    </button>
                  )}
                </div>
                {!isPro ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: t.textMuted, fontSize: '14px' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#0061a4', marginBottom: '6px' }}>{fmtDol(endOfDayBalance)}</div>
                    <div style={{ fontSize: '12px', color: t.textMuted }}>Current balance snapshot</div>
                    <div style={{ marginTop: '12px', fontSize: '13px', color: t.textMuted }}>
                      Upgrade to Pro to see 3, 6, or 12-month projections with confidence bands.
                    </div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={230}>
                    <AreaChart data={projectionData} margin={{ top: 5, right: 24, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtK} width={52} />
                      <Tooltip formatter={(v) => fmtDol(v)} />
                      <Area
                        type="monotone"
                        dataKey="Optimistic"
                        stroke="#1b6d24"
                        strokeWidth={1}
                        strokeDasharray="4 2"
                        fill="rgba(27,109,36,0.06)"
                        name="Optimistic"
                      />
                      <Area
                        type="monotone"
                        dataKey="Conservative"
                        stroke="#ba1a1a"
                        strokeWidth={1}
                        strokeDasharray="4 2"
                        fill="rgba(186,26,26,0.06)"
                        name="Conservative"
                      />
                      <Line
                        type="monotone"
                        dataKey="Balance"
                        stroke="#0061a4"
                        strokeWidth={2.5}
                        dot={{ r: 3, fill: '#0061a4' }}
                        activeDot={{ r: 5 }}
                        name="Projected"
                      />
                      {totalDebt > 0 && (
                        <Line
                          type="monotone"
                          dataKey="NetWorth"
                          stroke="#7c4dff"
                          strokeWidth={2}
                          strokeDasharray="5 3"
                          dot={{ r: 2.5, fill: '#7c4dff' }}
                          name="Net Worth"
                        />
                      )}
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Expense donut */}
              <div style={{ ...s.chartCard, background: t.card, flex: 1, minWidth: isMobile ? '100%' : '220px' }}>
                <h3 style={{ ...s.chartTitle, color: t.text, fontSize: sectionTitleFontSize }}>Expense Breakdown</h3>
                {expenseSlices.length === 0 ? (
                  <div style={{ ...s.emptyChart, color: t.textFaintest }}>No expenses yet</div>
                ) : (
                  <ResponsiveContainer width="100%" height={230}>
                    <PieChart>
                      <Pie
                        data={expenseSlices}
                        cx="50%"
                        cy="45%"
                        innerRadius={52}
                        outerRadius={80}
                        dataKey="value"
                        nameKey="name"
                        paddingAngle={2}
                      >
                        {expenseSlices.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => fmtDol(v) + '/mo'} />
                      <Legend iconSize={9} wrapperStyle={{ fontSize: '11px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Budget vs Actual chart — Pro + spending data only */}
            {isPro && (() => {
              const thisMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
              const monthLog = spendingLog.filter(e => e.date && e.date.startsWith(thisMonth) && e.type === 'expense');
              if (monthLog.length === 0) return (
                <div style={{ ...s.chartCard, background: t.card, marginBottom: '16px' }}>
                  <h3 style={{ ...s.chartTitle, color: t.text, fontSize: sectionTitleFontSize, marginBottom: '8px' }}>Budget vs. Actual</h3>
                  <p style={{ color: t.textMuted, fontSize: '13px', margin: 0 }}>Start logging your spending to see how your budget compares to reality.</p>
                </div>
              );
              // Build category data
              const categories = {};
              budgetItems.filter(i => i.type !== 'income' && i.amount > 0).forEach(i => {
                const cat = i.category || 'Other Expenses';
                categories[cat] = (categories[cat] || 0) + toMonthly(i);
              });
              monthLog.forEach(e => {
                const cat = e.category || 'Other Expenses';
                if (!categories[cat]) categories[cat] = 0;
              });
              const chartData = Object.entries(categories).map(([cat, budgeted]) => {
                const actual = monthLog.filter(e => (e.category || 'Other Expenses') === cat).reduce((s, e) => s + e.amount, 0);
                return { name: cat.length > 12 ? cat.slice(0, 11) + '…' : cat, Budgeted: parseFloat(budgeted.toFixed(2)), Actual: parseFloat(actual.toFixed(2)) };
              }).filter(d => d.Budgeted > 0 || d.Actual > 0);
              if (chartData.length === 0) return null;
              return (
                <div style={{ ...s.chartCard, background: t.card, marginBottom: '16px' }}>
                  <h3 style={{ ...s.chartTitle, color: t.text, fontSize: sectionTitleFontSize, marginBottom: '14px' }}>Budget vs. Actual — {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#30363d' : '#f0f0f0'} />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: t.textMuted }} angle={-35} textAnchor="end" interval={0} />
                      <YAxis tick={{ fontSize: 10, fill: t.textMuted }} tickFormatter={fmtK} width={48} />
                      <Tooltip formatter={(v) => fmtDol(v)} contentStyle={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: '6px' }} />
                      <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
                      <Bar dataKey="Budgeted" fill="#0061a4" radius={[3,3,0,0]} name="Budgeted" />
                      <Bar dataKey="Actual" fill="#ba1a1a" radius={[3,3,0,0]} name="Actual" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              );
            })()}

            {/* ── Cash flow bar chart ── */}
            {isPro && projMonths > 0 && (
            <div style={{ ...s.chartCard, background: t.card }}>
              <h3 style={{ ...s.chartTitle, color: t.text, fontSize: sectionTitleFontSize }}>Monthly Cash Flow — {projMonths}-Month View</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={projectionData} margin={{ top: 5, right: 24, left: 0, bottom: 0 }} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtK} width={52} />
                  <Tooltip formatter={(v) => fmtDol(v)} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="Income"   fill="#1b6d24" radius={[3, 3, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="Expenses" fill="#ba1a1a" radius={[3, 3, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            )}

            {/* ── Upcoming bills (next 30 days) ── */}
            {upcoming.length > 0 && (
              <div style={{ ...s.chartCard, background: t.card }}>
                <h3 style={{ ...s.chartTitle, color: t.text, fontSize: sectionTitleFontSize }}>Upcoming in the Next 30 Days</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: t.tableHeader }}>
                        <Th padding={thPadding} fontSize={tableFontSize}>Name</Th>
                        <Th padding={thPadding} fontSize={tableFontSize}>Type</Th>
                        <Th padding={thPadding} fontSize={tableFontSize}>Amount</Th>
                        <Th padding={thPadding} fontSize={tableFontSize}>Frequency</Th>
                        <Th padding={thPadding} fontSize={tableFontSize}>Due Date</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {upcoming.map((item, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${t.border}` }}>
                          <Td padding={tdPadding} fontSize={tableFontSize}>{item.name}</Td>
                          <Td padding={tdPadding} fontSize={tableFontSize}>
                            <span style={{
                              fontSize: '10px', fontWeight: '700', padding: '2px 8px',
                              borderRadius: '20px',
                              background: item.type === 'income' ? 'rgba(27,109,36,0.12)' : 'rgba(186,26,26,0.12)',
                              color: item.type === 'income' ? '#1b6d24' : '#ba1a1a',
                            }}>
                              {item.type === 'income' ? 'Income' : 'Expense'}
                            </span>
                          </Td>
                          <Td padding={tdPadding} fontSize={tableFontSize} style={{ fontWeight: '600', color: item.type === 'income' ? '#1b6d24' : '#ba1a1a' }}>
                            {fmtDol(item.amount)}
                          </Td>
                          <Td padding={tdPadding} fontSize={tableFontSize}>{item.frequency}</Td>
                          <Td padding={tdPadding} fontSize={tableFontSize}>{item.dueDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Debt overview ── */}
            {debts.length > 0 && (
              <div style={{ ...s.chartCard, background: t.card }}>
                <h3 style={{ ...s.chartTitle, color: t.text, fontSize: sectionTitleFontSize }}>Debt Overview</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: t.tableHeader }}>
                        <Th padding={thPadding} fontSize={tableFontSize}>Name</Th>
                        <Th padding={thPadding} fontSize={tableFontSize}>Balance</Th>
                        <Th padding={thPadding} fontSize={tableFontSize}>Interest Rate</Th>
                        <Th padding={thPadding} fontSize={tableFontSize}>Min. Payment</Th>
                        <Th padding={thPadding} fontSize={tableFontSize}>Est. Monthly Interest</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {debts.map((d) => (
                        <tr key={d.id} style={{ borderBottom: `1px solid ${t.border}` }}>
                          <Td padding={tdPadding} fontSize={tableFontSize}>{d.name}</Td>
                          <Td padding={tdPadding} fontSize={tableFontSize} style={{ fontWeight: '700', color: '#e65100' }}>{fmtDol(d.balance)}</Td>
                          <Td padding={tdPadding} fontSize={tableFontSize}>{d.interestRate}%</Td>
                          <Td padding={tdPadding} fontSize={tableFontSize}>{fmtDol(d.minPayment)}</Td>
                          <Td padding={tdPadding} fontSize={tableFontSize} style={{ color: '#ba1a1a' }}>{fmtDol((d.balance * (d.interestRate / 100)) / 12)}</Td>
                        </tr>
                      ))}
                      <tr style={{ background: t.border, fontWeight: '700' }}>
                        <Td padding={tdPadding} fontSize={tableFontSize}>Total</Td>
                        <Td padding={tdPadding} fontSize={tableFontSize} style={{ color: '#e65100', fontWeight: '700' }}>{fmtDol(totalDebt)}</Td>
                        <Td padding={tdPadding} fontSize={tableFontSize}>—</Td>
                        <Td padding={tdPadding} fontSize={tableFontSize}>{fmtDol(debts.reduce((s, d) => s + d.minPayment, 0))}</Td>
                        <Td padding={tdPadding} fontSize={tableFontSize} style={{ color: '#ba1a1a' }}>{fmtDol(debts.reduce((s, d) => s + (d.balance * (d.interestRate / 100)) / 12, 0))}</Td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function StatCard({ label, value, accent, sub }) {
  const { t, isDark } = useTheme();
  const effectiveAccent = isDark ? accent : accent;
  return (
    <div style={{ ...s.statCard, background: t.card, borderLeft: `4px solid ${effectiveAccent}` }}>
      <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase', color: t.textSecondary, marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: '20px', fontWeight: '700', color: t.text }}>{value}</div>
      {sub && <div style={{ fontSize: '11px', color: t.textMuted, marginTop: '4px', fontWeight: '500' }}>{sub}</div>}
    </div>
  );
}

function Th({ children, padding, fontSize }) {
  const { t } = useTheme();
  return <th style={{ padding: padding || '9px 14px', textAlign: 'left', fontSize: '10px', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase', color: t.textSecondary }}>{children}</th>;
}
function Td({ children, style, padding, fontSize }) {
  return <td style={{ padding: padding || '10px 14px', fontSize: fontSize || '13px', ...style }}>{children}</td>;
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  page: {
    minHeight: '100vh',
    padding: '20px',
  },
  container: {
    maxWidth: '1100px',
    margin: '0 auto',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: '800',
    letterSpacing: '-0.02em',
    margin: '0 0 20px',
  },
  cardsRow: {
    display: 'grid',
    gap: '14px',
    marginBottom: '20px',
  },
  statCard: {
    borderRadius: '12px',
    padding: '18px 20px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  chartsRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  chartCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px 24px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    marginBottom: '16px',
  },
  chartTitle: {
    margin: '0 0 14px',
    fontSize: '15px',
    fontWeight: '700',
  },
  emptyChart: {
    height: '230px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#bfc7d4',
    fontSize: '14px',
  },
  emptyState: {
    background: 'white',
    borderRadius: '12px',
    padding: '48px',
    textAlign: 'center',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
};
